import {
    AppMessageTemplateButtonConfig,
    MutableAppMessageTemplateButtonConfig,
} from '@pushly/models/lib/structs/app-messages/app-message-template-button-config'
import React, { useState } from 'react'
import { AppMessageCtaBuilder } from './app-message-cta-builder'
import { MutableAppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import { AppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import { CtaButtonType, CtaGroupTuple } from '../types'
import { InfoTooltip } from '../../info-tooltip/info-tooltip'
import { AppMessageTemplateStyle } from '@pushly/models/lib/enums'
import { LayoutOrientation } from '@pushly/models/lib/enums/layout-orientation'
import titleCase from 'title-case'
import { Button, Radio, Tooltip } from 'antd'
import { CloseOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import { AppMessageCtaRow } from './app-message-cta-row'

type AppMessageCtaBuilderProps = {
    value: MutableAppMessageUiTemplate
    onChange: (change: Partial<AppMessageTemplateButtonConfig>, action: CtaButtonType) => void
    /** For template-level properties a button row owns — layout and the close action. */
    onTemplateChange: (change: Partial<AppMessageUiTemplate>) => void
    disabled: boolean
}

/** Which row is open for editing, or none. */
type OpenCtaRow = CtaButtonType | 'CLOSE' | null

export const AppMessageCtasBuilder = (props: AppMessageCtaBuilderProps) => {
    const { value, onChange, onTemplateChange, disabled } = props

    const [openRow, setOpenRow] = useState<OpenCtaRow>(null)

    const ctaPrimary = value.ctaPrimary ?? new MutableAppMessageTemplateButtonConfig()
    const ctaSecondary = value.ctaSecondary ?? new MutableAppMessageTemplateButtonConfig()

    const ctas: CtaGroupTuple[] = [
        [CtaButtonType.PRIMARY, ctaPrimary],
        [CtaButtonType.SECONDARY, ctaSecondary],
    ]

    const isBanner = value.style === AppMessageTemplateStyle.BANNER
    const isFullscreen = value.style === AppMessageTemplateStyle.FULLSCREEN

    // A banner has room for one action; anywhere else a second button can follow
    // the first. The primary is always the one added next, so its row and the add
    // control are never both present.
    const nextCtaToAdd = !ctaPrimary.enabled
        ? CtaButtonType.PRIMARY
        : !isBanner && !ctaSecondary.enabled
          ? CtaButtonType.SECONDARY
          : undefined

    return (
        <div className={getClassNames('app-message-ctas')}>
            <div className="content-block-head">
                <span className="content-block-name">
                    <span className="content-block-label">Buttons</span>
                    <InfoTooltip
                        about="Buttons"
                        title="The actions a reader can take. A button can open a URL, ask for notification permission, or dismiss the message."
                    />
                </span>

                {!isBanner && (
                    <span className="content-block-toolbar button-layout-row">
                        <span className="button-layout-label">Button Layout</span>
                        <Radio.Group
                            disabled={disabled}
                            buttonStyle="solid"
                            optionType="button"
                            size="small"
                            className="button-layout"
                            onChange={(ev) => onTemplateChange({ ctaLayout: ev.target.value })}
                            value={value.ctaLayout ?? LayoutOrientation.VERTICAL}
                            options={Object.values(LayoutOrientation).map((orientation) => ({
                                label: titleCase(orientation),
                                value: LayoutOrientation[orientation],
                            }))}
                        />
                    </span>
                )}
            </div>

            {ctas
                // A button that does not exist yet is offered by the add control
                // below, so a dimmed row for it would be a second way to do the
                // same thing.
                .filter(([_, cta]) => cta.enabled)
                .map(([type, cta]) => (
                    <AppMessageCtaBuilder
                        key={type}
                        type={type}
                        value={[type, cta]}
                        rootCtas={ctas}
                        onChange={onChange}
                        disabled={disabled}
                        templateStyle={value.style}
                        expanded={openRow === type}
                        onExpandedChange={(expanded) => setOpenRow(expanded ? type : null)}
                    />
                ))}

            {!!nextCtaToAdd && (
                <Button
                    className="add-cta"
                    type="dashed"
                    block
                    disabled={disabled}
                    icon={<PlusOutlined />}
                    onClick={() => {
                        onChange({ enabled: true }, nextCtaToAdd)
                        setOpenRow(nextCtaToAdd)
                    }}
                >
                    {nextCtaToAdd === CtaButtonType.PRIMARY ? 'Add primary button' : 'Add secondary button'}
                </Button>
            )}

            {isFullscreen && (
                <AppMessageCtaRow
                    className="cta-close-action"
                    label="Close Button"
                    tag="Dismiss"
                    info="When enabled, a dedicated close button will be added in the top right corner of the app message."
                    summary={
                        value.showCloseAction
                            ? 'Shown in the top corner of the message'
                            : 'Not shown — people dismiss using the buttons above'
                    }
                    backgroundColor="rgba(0, 0, 0, 0.35)"
                    foregroundColor="#FFFFFF"
                    swatchIcon={<CloseOutlined />}
                    // The close action is a property of the template rather than
                    // a button that can be added, so it switches rather than
                    // being removed.
                    toggle={{
                        checked: value.showCloseAction,
                        disabled,
                        onChange: (showCloseAction) => onTemplateChange({ showCloseAction }),
                    }}
                />
            )}
        </div>
    )
}
